import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { ActivatedRoute, Router } from '@angular/router';
import {
  LicensedComponentId,
  LicensedProductDto,
} from 'src/app/models/license.model';
import { LicensedProductService } from 'src/app/services/licensed-product.service';
import { LicensedProductStore } from 'src/app/services/licensed-product.store';
import { matchesSearch } from 'src/app/utils/text-search';
import { LicensedComponentCardItem } from '../license-component-card/license-component-card.component';

@Component({
  selector: 'app-license-product-form',
  templateUrl: './license-product-form.component.html',
  styleUrls: ['./license-product-form.component.scss'],
  standalone: false,
})
export class LicenseProductFormComponent implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly licensedProductService = inject(LicensedProductService);
  private readonly licensedProductStore = inject(LicensedProductStore);

  form: FormGroup = this.formBuilder.nonNullable.group({
    name: this.formBuilder.control('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(120)],
    }),
    description: this.formBuilder.control('', {
      nonNullable: true,
      validators: [Validators.maxLength(500)],
    }),
    target: this.formBuilder.control<'DEVICE' | 'USER'>('DEVICE', {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });
  componentSearchControl = this.formBuilder.control('', { nonNullable: true });
  readonly availableComponentsPageSizeOptions = [12, 24, 48];
  availableComponentsPageSize = 12;
  availableComponentsPageIndex = 0;

  selectedComponents: LicensedComponentId[] = [];
  editingProductId: string | null = null;
  readonly componentsLoading = this.licensedProductStore.loading;
  readonly componentsLoadError = this.licensedProductStore.error;
  loading = false;
  loadFailed = false;
  saving = false;
  showResponseBanner = false;
  successfulSave = false;
  successMessage = 'Licensed product saved.';
  errorMessage = 'Failed to save licensed product.';

  get isEditMode(): boolean {
    return this.editingProductId !== null;
  }

  get nameControl() {
    return this.form.controls['name'];
  }

  get availableComponents(): LicensedComponentId[] {
    return this.licensedProductStore
      .availableComponents()
      .map((component) => component.id as LicensedComponentId);
  }

  get availableComponentCards(): LicensedComponentCardItem[] {
    return this.licensedProductStore.availableComponents().map((component) => ({
      ...component,
      id: component.id as LicensedComponentId,
    }));
  }

  // Built from selectedComponents so components missing from the available
  // list are still shown (by id) and can be removed.
  get allSelectedComponentCards(): LicensedComponentCardItem[] {
    const cardsById = new Map(
      this.availableComponentCards.map((card) => [card.id, card]),
    );
    return this.selectedComponents.map(
      (id) => cardsById.get(id) ?? { id, componentName: id, description: '' },
    );
  }

  get allUnselectedComponentCards(): LicensedComponentCardItem[] {
    return this.availableComponentCards.filter(
      (component) => !this.isComponentSelected(component.id),
    );
  }

  get selectedComponentCards(): LicensedComponentCardItem[] {
    return this.allSelectedComponentCards.filter((component) =>
      this.matchesComponentSearch(component),
    );
  }

  get unselectedComponentCards(): LicensedComponentCardItem[] {
    return this.allUnselectedComponentCards.filter((component) =>
      this.matchesComponentSearch(component),
    );
  }

  get hiddenSelectedComponentCount(): number {
    return (
      this.allSelectedComponentCards.length - this.selectedComponentCards.length
    );
  }

  get hiddenSelectedComponentsMessage(): string {
    const count = this.hiddenSelectedComponentCount;
    const search = this.componentSearchControl.value.trim();
    return count === 1
      ? `1 more selected component doesn't match "${search}".`
      : `${count} more selected components don't match "${search}".`;
  }

  get paginatedUnselectedComponentCards(): LicensedComponentCardItem[] {
    const start =
      this.availableComponentsPageIndex * this.availableComponentsPageSize;
    return this.unselectedComponentCards.slice(
      start,
      start + this.availableComponentsPageSize,
    );
  }

  get totalAvailableComponentPages(): number {
    return Math.max(
      1,
      Math.ceil(
        this.unselectedComponentCards.length / this.availableComponentsPageSize,
      ),
    );
  }

  formatComponentCount(shown: number, total: number): string {
    return shown === total ? `${total}` : `${shown}/${total}`;
  }

  ngOnInit(): void {
    this.licensedProductStore.getComponentsForLicensedProduct();
    const productId = this.route.snapshot.paramMap.get('id');
    if (!productId) {
      return;
    }
    this.editingProductId = productId;
    this.loadProduct(productId);
  }

  // Getters rebuild card objects on every check; without this ngFor recreates
  // the cards mid-click (e.g. on input/paginator blur) and the click is lost.
  trackByComponentId(
    _index: number,
    component: { id: LicensedComponentId },
  ): LicensedComponentId {
    return component.id;
  }

  isComponentSelected(component: LicensedComponentId): boolean {
    return this.selectedComponents.includes(component);
  }

  toggleComponent(component: LicensedComponentId, selected: boolean): void {
    if (selected) {
      if (!this.selectedComponents.includes(component)) {
        this.selectedComponents = [...this.selectedComponents, component];
      }
      return;
    }

    this.selectedComponents = this.selectedComponents.filter(
      (existingComponent) => existingComponent !== component,
    );
  }

  onComponentCardToggle(component: LicensedComponentId): void {
    this.toggleComponent(component, !this.isComponentSelected(component));
    this.clampAvailableComponentsPage();
  }

  resetAvailableComponentsPagination(): void {
    this.availableComponentsPageIndex = 0;
  }

  onAvailableComponentsPageChange(event: PageEvent): void {
    this.availableComponentsPageIndex = event.pageIndex;
    this.availableComponentsPageSize = event.pageSize;
  }

  submit(): void {
    if (this.saving) {
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const normalizedName = this.form.controls['name'].value.trim();
    if (!normalizedName) {
      this.form.controls['name'].setErrors({ required: true });
      return;
    }

    const payload: LicensedProductDto = {
      name: normalizedName,
      description: this.form.controls['description'].value.trim() || undefined,
      target: this.form.controls['target'].value,
      licensedComponents: this.selectedComponents.map((id) => ({ id })),
    };

    this.saving = true;
    this.showResponseBanner = false;

    if (!this.isEditMode) {
      this.licensedProductService.create(payload).subscribe({
        next: () => {
          this.handleSaveSuccess('Licensed product created.');
        },
        error: (error: unknown) => {
          this.handleSaveError(error, 'Failed to create licensed product.');
        },
      });
      return;
    }

    this.licensedProductService
      .update(this.editingProductId!, payload)
      .subscribe({
        next: () => {
          this.handleSaveSuccess('Licensed product updated.');
        },
        error: (error: unknown) => {
          this.handleSaveError(error, 'Failed to update licensed product.');
        },
      });
  }

  cancel(): void {
    this.router.navigate(['/admin/licenses/products']);
  }

  private loadProduct(productId: string): void {
    this.loading = true;
    this.licensedProductService.getById(productId).subscribe({
      next: (product) => {
        this.form.patchValue({
          name: product.name ?? '',
          description: product.description ?? '',
          target: product.target ?? 'DEVICE',
        });
        const selectedComponentIds =
          product.licensedComponents?.map((component) => component.id) ?? [];
        this.selectedComponents = [...selectedComponentIds];
        this.loading = false;
      },
      error: (error: unknown) => {
        this.errorMessage = this.resolveErrorMessage(
          error,
          'Failed to load licensed product.',
        );
        this.loadFailed = true;
        this.loading = false;
      },
    });
  }

  private handleSaveSuccess(message: string): void {
    this.successfulSave = true;
    this.successMessage = message;
    this.showResponseBanner = true;
    // saving stays true until navigation so the form can't be resubmitted.
    setTimeout(() => {
      this.router.navigate(['/admin/licenses/products']);
    }, 600);
  }

  private handleSaveError(error: unknown, fallbackMessage: string): void {
    this.successfulSave = false;
    this.errorMessage = this.resolveErrorMessage(error, fallbackMessage);
    this.showResponseBanner = true;
    this.saving = false;
  }

  private resolveErrorMessage(error: unknown, fallbackMessage: string): string {
    if (!error) {
      return fallbackMessage;
    }
    if (typeof error === 'string') {
      return error;
    }
    if (typeof error === 'object' && error !== null) {
      const typedError = error as {
        error?: { message?: string; title?: string };
        message?: string;
      };
      if (typedError.error?.message) {
        return typedError.error.message;
      }
      if (typedError.error?.title) {
        return typedError.error.title;
      }
      if (typedError.message) {
        return typedError.message;
      }
    }
    return fallbackMessage;
  }

  private matchesComponentSearch(
    component: LicensedComponentCardItem,
  ): boolean {
    return matchesSearch(this.componentSearchControl.value, [
      component.componentName || component.id,
    ]);
  }

  private clampAvailableComponentsPage(): void {
    const maxPageIndex = Math.max(this.totalAvailableComponentPages - 1, 0);
    if (this.availableComponentsPageIndex > maxPageIndex) {
      this.availableComponentsPageIndex = maxPageIndex;
    }
  }
}
